import { useState, useEffect } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  BarChart3,
  IndianRupee,
  FileText,
  ArrowLeft,
  Loader2,
  CalendarIcon,
  Building2,
  Filter,
  TrendingUp,
  Users,
  RefreshCw,
  PieChart,
  Smartphone,
  X,
  Lock,
  Eye,
  EyeOff,
  CalendarDays,
  Mail,
  Send,
  Heart,
  User,
  Car,
  BookOpen,
  CalendarCheck,
  CalendarClock,
  Target,
  Repeat,
  ChevronDown,
  ChevronUp,
  Edit2,
  ExternalLink
} from "lucide-react";
import { authGet, authPost, authPut, getAuthToken } from "../utils/auth";
import { 
  BarChart as ReBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart as RePieChart, Pie, Cell, Legend
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BusinessDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [reportEmail, setReportEmail] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  
  // Commission visibility state
  const [commissionUnlocked, setCommissionUnlocked] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [sendingReport, setSendingReport] = useState(false);
  
  // Insurance Book Projection visibility
  const [showInsuranceBook, setShowInsuranceBook] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    dateFrom: "",
    dateTo: "",
    platform: "all",
    policyType: "all",
    company: "all",
    referredBy: "all",
    insuranceCategory: "all"
  });
  
  // Analytics data
  const [analytics, setAnalytics] = useState({
    totalPolicies: 0,
    totalPremium: 0,
    totalCommission: 0,
    // NEW: Insurance Book Metrics
    firstYearCommissionable: 0,
    nextYearConfirmed: 0,      // From multi-year policies
    nextYearExpectedRenewals: 0, // From annual policies
    year2PlusPipeline: 0,      // Future confirmed from multi-year
    annualPolicyCount: 0,
    multiYearPolicyCount: 0,
    byPlatform: {},
    byPolicyType: {},
    byCompany: {},
    byReferredBy: {},
    byMonth: {},
    byInsuranceCategory: {}
  });
  
  // Drill-down modal state
  const [drillDownModal, setDrillDownModal] = useState({
    show: false,
    title: "",
    filterType: "",
    filterValue: "",
    policies: []
  });
  
  // Chart analytics data
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(true);
  
  // Commission PIN verification
  const COMMISSION_PIN = "2111";
  
  const handleUnlockCommission = () => {
    if (pinInput === COMMISSION_PIN) {
      setCommissionUnlocked(true);
      setShowPinModal(false);
      setPinInput("");
      setPinError("");
    } else {
      setPinError("Incorrect PIN. Please try again.");
      setPinInput("");
    }
  };

  const saveReportEmail = async () => {
    const email = emailInput.trim();
    if (!email.includes('@')) { toast.error('Enter a valid email address'); return; }
    const res = await authPut('/api/advisor/report-email', { email });
    if (res.ok) {
      setReportEmail(res.data?.report_email || email);
      setEditingEmail(false);
      toast.success('Report recipient updated');
    } else {
      toast.error(res.data?.detail || 'Failed to update recipient');
    }
  };

  const handleSendReport = async () => {    setSendingReport(true);
    try {
      const res = await authPost('/api/advisor/send-business-report', {});
      
      if (res.ok) {
        toast.success("Business report sent to your email!");
      } else {
        toast.error(res.data?.detail || "Failed to send report");
      }
    } catch (err) {
      console.error("Error sending report:", err);
      toast.error("Failed to send report");
    } finally {
      setSendingReport(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, policies]);

  const fetchData = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        navigate('/advisor/login');
        return;
      }
      
      const authRes = await authGet('/api/auth/me');
      if (!authRes.ok) {
        navigate('/advisor/login');
        return;
      }

      const res = await authGet('/api/advisor/analytics/revenue');
      if (res.ok) {
        setPolicies(res.data?.policy_details || []);
      }

      // Total clients (moved here from the home dashboard)
      const statsRes = await authGet('/api/advisor/dashboard');
      if (statsRes.ok) {
        setTotalClients(statsRes.data?.total_clients || 0);
      }

      // Report recipient (shown in the automation banner)
      const schedRes = await authGet('/api/advisor/scheduler-status');
      if (schedRes.ok) {
        setReportEmail(schedRes.data?.report_email || '');
      }
      
      // Fetch chart analytics
      const chartRes = await authGet('/api/advisor/analytics/charts');
      if (chartRes.ok) {
        setChartData(chartRes.data);
      }
      setChartLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...policies];

    // Date filter
    if (filters.dateFrom) {
      filtered = filtered.filter(p => p.policy_start_date >= filters.dateFrom);
    }
    if (filters.dateTo) {
      filtered = filtered.filter(p => p.policy_start_date <= filters.dateTo);
    }

    // Platform filter
    if (filters.platform !== "all") {
      filtered = filtered.filter(p => p.platform === filters.platform);
    }

    // Policy type filter
    if (filters.policyType !== "all") {
      filtered = filtered.filter(p => p.policy_type === filters.policyType);
    }

    // Company filter
    if (filters.company !== "all") {
      filtered = filtered.filter(p => p.insurance_company === filters.company);
    }

    // Referred by filter
    if (filters.referredBy !== "all") {
      filtered = filtered.filter(p => p.referred_by === filters.referredBy);
    }

    // Insurance Category filter
    if (filters.insuranceCategory !== "all") {
      filtered = filtered.filter(p => (p.insurance_category || "Health") === filters.insuranceCategory);
    }

    setFilteredPolicies(filtered);
    calculateAnalytics(filtered);
  };

  const calculateAnalytics = (data) => {
    const byPlatform = {};
    const byPolicyType = {};
    const byCompany = {};
    const byReferredBy = {};
    const byMonth = {};
    const byInsuranceCategory = {};
    
    let totalPremium = 0;
    let totalCommission = 0;
    
    // NEW: Insurance Book calculations
    let firstYearCommissionable = 0;
    let nextYearConfirmed = 0;
    let nextYearExpectedRenewals = 0;
    let year2PlusPipeline = 0;
    let annualPolicyCount = 0;
    let multiYearPolicyCount = 0;

    // Helper function to calculate policy term in years
    const getPolicyTermYears = (startDate, endDate) => {
      if (!startDate || !endDate) return 1;
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffMs = end - start;
      const diffYears = diffMs / (365.25 * 24 * 60 * 60 * 1000);
      return Math.max(1, Math.round(diffYears));
    };

    data.forEach(policy => {
      const premium = parseFloat(policy.premium_amount) || 0;
      const commission = parseFloat(policy.mis_received_total) || 0; // actual received via MIS reconciliation
      
      totalPremium += premium;
      totalCommission += commission;

      // Calculate policy term and yearly premium
      const policyTermYears = getPolicyTermYears(policy.policy_start_date, policy.policy_end_date);
      const yearlyPremium = premium / policyTermYears;
      
      // First Year Commissionable = Premium / Policy Years
      firstYearCommissionable += yearlyPremium;
      
      if (policyTermYears === 1) {
        // Annual policy - expected to renew next year
        annualPolicyCount++;
        nextYearExpectedRenewals += premium; // Full premium expected next year
      } else {
        // Multi-year policy
        multiYearPolicyCount++;
        // Next year is CONFIRMED (if policy has 2+ years)
        if (policyTermYears >= 2) {
          nextYearConfirmed += yearlyPremium;
        }
        // Year 2+ pipeline (for 3+ year policies, the remaining years after next)
        if (policyTermYears >= 3) {
          year2PlusPipeline += yearlyPremium * (policyTermYears - 2);
        }
      }

      // By Platform
      const platform = policy.platform || "Direct";
      if (!byPlatform[platform]) {
        byPlatform[platform] = { count: 0, premium: 0, commission: 0 };
      }
      byPlatform[platform].count++;
      byPlatform[platform].premium += premium;
      byPlatform[platform].commission += commission;

      // By Policy Type
      const policyType = policy.policy_type || "Fresh";
      if (!byPolicyType[policyType]) {
        byPolicyType[policyType] = { count: 0, premium: 0, commission: 0 };
      }
      byPolicyType[policyType].count++;
      byPolicyType[policyType].premium += premium;
      byPolicyType[policyType].commission += commission;

      // By Company
      const company = policy.insurance_company || "Unknown";
      if (!byCompany[company]) {
        byCompany[company] = { count: 0, premium: 0, commission: 0 };
      }
      byCompany[company].count++;
      byCompany[company].premium += premium;
      byCompany[company].commission += commission;

      // By Referred By
      const referredBy = policy.referred_by || "Direct";
      if (!byReferredBy[referredBy]) {
        byReferredBy[referredBy] = { count: 0, premium: 0, commission: 0 };
      }
      byReferredBy[referredBy].count++;
      byReferredBy[referredBy].premium += premium;
      byReferredBy[referredBy].commission += commission;

      // By Month
      const month = policy.policy_issue_month || "Unknown";
      if (!byMonth[month]) {
        byMonth[month] = { count: 0, premium: 0, commission: 0 };
      }
      byMonth[month].count++;
      byMonth[month].premium += premium;
      byMonth[month].commission += commission;

      // By Insurance Category
      const category = policy.insurance_category || "Health";
      if (!byInsuranceCategory[category]) {
        byInsuranceCategory[category] = { count: 0, premium: 0, commission: 0 };
      }
      byInsuranceCategory[category].count++;
      byInsuranceCategory[category].premium += premium;
      byInsuranceCategory[category].commission += commission;
    });

    setAnalytics({
      totalPolicies: data.length,
      totalPremium,
      totalCommission,
      // NEW: Insurance Book Metrics
      firstYearCommissionable,
      nextYearConfirmed,
      nextYearExpectedRenewals,
      year2PlusPipeline,
      annualPolicyCount,
      multiYearPolicyCount,
      byPlatform,
      byPolicyType,
      byCompany,
      byReferredBy,
      byMonth,
      byInsuranceCategory
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      platform: "all",
      policyType: "all",
      company: "all",
      referredBy: "all",
      insuranceCategory: "all"
    });
  };

  const hasActiveFilters = () => {
    return filters.dateFrom || filters.dateTo || 
           filters.platform !== "all" || filters.policyType !== "all" || 
           filters.company !== "all" || filters.referredBy !== "all" || 
           filters.insuranceCategory !== "all";
  };

  // Get unique values for filter dropdowns
  const uniquePlatforms = [...new Set(policies.map(p => p.platform || "Direct"))];
  const uniqueCompanies = [...new Set(policies.map(p => p.insurance_company))].filter(Boolean);
  const uniqueReferredBy = [...new Set(policies.map(p => p.referred_by || "Direct"))];

  // Sort analytics by count for display
  const sortedByPlatform = Object.entries(analytics.byPlatform).sort((a, b) => b[1].count - a[1].count);
  const sortedByCompany = Object.entries(analytics.byCompany).sort((a, b) => b[1].premium - a[1].premium);
  const sortedByMonth = Object.entries(analytics.byMonth).sort((a, b) => b[0].localeCompare(a[0]));

  // Handle drill-down click - show policies for a specific filter
  const handleDrillDown = (filterType, filterValue, displayTitle) => {
    let matchingPolicies = filteredPolicies.filter(policy => {
      switch (filterType) {
        case "policyType":
          return (policy.policy_type || "Fresh") === filterValue;
        case "referredBy":
          return (policy.referred_by || "Direct") === filterValue;
        case "platform":
          return (policy.platform || "Direct") === filterValue;
        case "company":
          return policy.insurance_company === filterValue;
        case "category":
          return (policy.insurance_category || "Health") === filterValue;
        default:
          return false;
      }
    });

    setDrillDownModal({
      show: true,
      title: displayTitle,
      filterType,
      filterValue,
      policies: matchingPolicies
    });
  };

  const closeDrillDown = () => {
    setDrillDownModal({
      show: false,
      title: "",
      filterType: "",
      filterValue: "",
      policies: []
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-[#A87608] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFFFF] to-[#EDF1F8] p-4 sm:p-6" data-testid="business-dashboard">
      {/* PIN Modal for Commission */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowPinModal(false)}>
          <div 
            className="bg-[#EDF1F8] border border-[#C9D4E6] rounded-2xl p-6 w-full max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#1B2A4A] flex items-center gap-2">
                <Lock className="h-5 w-5 text-[#A87608]" />
                Unlock Commission Data
              </h3>
              <button 
                onClick={() => { setShowPinModal(false); setPinInput(""); setPinError(""); }}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-[#3A4557] text-sm mb-4">Enter PIN to view commission details</p>
            <input
              type="password"
              value={pinInput}
              onChange={(e) => { setPinInput(e.target.value); setPinError(""); }}
              onKeyDown={(e) => e.key === 'Enter' && handleUnlockCommission()}
              placeholder="Enter 4-digit PIN"
              maxLength={4}
              className="w-full px-4 py-3 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-center text-2xl tracking-[1em] focus:outline-none focus:border-[#D4A017]"
              autoFocus
              data-testid="commission-pin-input"
            />
            {pinError && (
              <p className="text-red-600 text-sm mt-2 text-center">{pinError}</p>
            )}
            <Button
              onClick={handleUnlockCommission}
              className="w-full mt-4 bg-[#D4A017] hover:bg-[#D4A017] text-black font-semibold"
              data-testid="unlock-commission-btn"
            >
              <Eye className="h-4 w-4 mr-2" />
              Unlock
            </Button>
          </div>
        </div>
      )}

      {/* Drill-Down Modal - Shows policy details when clicking on a category */}
      {drillDownModal.show && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closeDrillDown}>
          <div 
            className="bg-[#EDF1F8] border border-[#C9D4E6] rounded-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-[#C9D4E6]">
              <h3 className="text-lg font-bold text-[#1B2A4A] flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#A87608]" />
                {drillDownModal.title}
                <span className="ml-2 px-2 py-0.5 bg-[#D4A017]/15 text-[#A87608] rounded-full text-sm">
                  {drillDownModal.policies.length} {drillDownModal.policies.length === 1 ? 'policy' : 'policies'}
                </span>
              </h3>
              <button 
                onClick={closeDrillDown}
                className="text-[#3A4557] hover:text-[#1B2A4A]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="overflow-auto flex-1 p-4">
              {drillDownModal.policies.length > 0 ? (
                <div className="space-y-3">
                  {drillDownModal.policies.map((policy, idx) => (
                    <div 
                      key={policy.policy_id || idx} 
                      className="bg-white/50 border border-[#C9D4E6] rounded-xl p-4 hover:border-[#D4A017]/50 transition-colors"
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[#1B2A4A] font-semibold text-lg">
                              {policy.client_name || "Unknown Client"}
                            </span>
                            {policy.policy_type && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                policy.policy_type === 'Fresh' ? 'bg-green-500/20 text-green-700' :
                                policy.policy_type === 'Portability' ? 'bg-purple-500/20 text-purple-700' :
                                'bg-[#EDF1F8]/40 text-[#2E3B57]'
                              }`}>
                                {policy.policy_type}
                              </span>
                            )}
                            {policy.insurance_category && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                policy.insurance_category === 'Health' ? 'bg-emerald-500/20 text-emerald-700' :
                                policy.insurance_category === 'Life' ? 'bg-[#EDF1F8]/40 text-[#2E3B57]' :
                                'bg-[#D4A017]/15 text-[#A87608]'
                              }`}>
                                {policy.insurance_category}
                              </span>
                            )}
                          </div>
                          <p className="text-[#3A4557] text-sm">
                            {policy.insurance_company} - {policy.plan_name || "N/A"}
                          </p>
                          <p className="text-[#3A4557] text-xs mt-1">
                            Policy #: {policy.policy_number || "N/A"} 
                            {policy.policy_start_date && ` • Started: ${new Date(policy.policy_start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-4 md:gap-6">
                          <div className="text-right">
                            <p className="text-[#3A4557] text-xs">Premium</p>
                            <p className="text-green-600 font-semibold">
                              {formatCurrency(policy.premium_amount)}
                            </p>
                          </div>
                          {commissionUnlocked && (
                            <div className="text-right">
                              <p className="text-[#3A4557] text-xs">Commission</p>
                              <p className="text-[#A87608] font-semibold">
                                {formatCurrency(policy.net_commission)}
                              </p>
                            </div>
                          )}
                          <div className="text-right">
                            <p className="text-[#3A4557] text-xs">Platform</p>
                            <p className="text-[#2E3B57] text-sm">
                              {policy.platform || "Direct"}
                            </p>
                          </div>
                          
                          {/* Edit & View Buttons */}
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-[#2E3B57] hover:text-[#2E3B57] hover:bg-[#EDF1F8]/40"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (policy.client_id) {
                                  closeDrillDown();
                                  navigate(`/advisor/clients/${policy.client_id}`);
                                }
                              }}
                              title="Edit Policy"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Referred By info if available */}
                      {policy.referred_by && policy.referred_by !== "Direct" && (
                        <div className="mt-2 pt-2 border-t border-[#C9D4E6]/50 flex items-center gap-2">
                          <Users className="h-3 w-3 text-[#3A4557]" />
                          <span className="text-[#3A4557] text-xs">Referred by: </span>
                          <span className="text-[#3A4557] text-xs">{policy.referred_by}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-[#3A4557] mx-auto mb-3" />
                  <p className="text-[#3A4557]">No policies found for this filter</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-[#C9D4E6] flex justify-end">
              <Button
                onClick={closeDrillDown}
                variant="outline"
                className="border-[#C9D4E6] text-[#2E3B57] hover:bg-[#E2E9F4]"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/advisor/dashboard')}
              className="text-[#3A4557] hover:text-[#1B2A4A]"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-[#1B2A4A]" data-testid="dashboard-title">Business Dashboard</h1>
              <p className="text-[#3A4557] text-sm">Analyze your insurance business performance</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleSendReport}
            disabled={sendingReport}
            className="border-green-500/50 text-green-600 hover:bg-green-500/10"
            data-testid="send-report-btn"
          >
            {sendingReport ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Mail className="h-4 w-4 mr-2" />
            )}
            {sendingReport ? "Sending..." : "Email Report"}
          </Button>
        </div>

        {/* Automated Report Info */}
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <CalendarDays className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-green-600 font-medium">📧 Automated Reports Active</p>
              {editingEmail ? (
                <div className="flex items-center gap-2 mt-1">
                  <input
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="recipient@email.com"
                    className="h-8 px-2 rounded border border-[#C9D4E6] text-sm text-[#1B2A4A] bg-white w-60"
                    data-testid="report-email-input"
                  />
                  <button onClick={saveReportEmail} className="h-8 px-3 rounded bg-green-600 text-white text-xs font-semibold" data-testid="report-email-save-btn">Save</button>
                  <button onClick={() => setEditingEmail(false)} className="h-8 px-2 rounded text-[#3A4557] text-xs">Cancel</button>
                </div>
              ) : (
                <p className="text-[#3A4557] text-sm">
                  Daily report (9:00 AM IST) &amp; weekly digest (Mondays) sent to{' '}
                  <span className="font-semibold text-[#1B2A4A]" data-testid="report-email-display">{reportEmail || 'your registered email'}</span>
                  <button onClick={() => { setEmailInput(reportEmail || ''); setEditingEmail(true); }} className="ml-2 text-green-700 underline text-xs font-medium" data-testid="report-email-change-btn">Change</button>
                </p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-[#3A4557] text-xs">Next report</p>
            <p className="text-green-600 font-medium">Tomorrow 9:00 AM</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="bg-white/50 border-[#C9D4E6] mb-6" data-testid="filters-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-[#A87608] flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters
              </CardTitle>
              {hasActiveFilters() && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-[#3A4557] hover:text-[#1B2A4A]"
                  data-testid="clear-filters-btn"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {/* Date From */}
              <div>
                <label className="text-[#3A4557] text-xs mb-1 block">From Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-9 justify-start text-left font-normal bg-white/70 border-[#C9D4E6] text-[#1B2A4A] hover:bg-[#EDF1F8]"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-[#3A4557]" />
                      {filters.dateFrom ? (
                        format(new Date(filters.dateFrom), "dd MMM yyyy")
                      ) : (
                        <span className="text-[#3A4557]">Select date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-[#C9D4E6]" align="start">
                    <CalendarPicker
                      mode="single"
                      selected={filters.dateFrom ? new Date(filters.dateFrom) : undefined}
                      onSelect={(date) => setFilters({...filters, dateFrom: date ? format(date, "yyyy-MM-dd") : ""})}
                      initialFocus
                      className="bg-white text-[#1B2A4A]"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Date To */}
              <div>
                <label className="text-[#3A4557] text-xs mb-1 block">To Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-9 justify-start text-left font-normal bg-white/70 border-[#C9D4E6] text-[#1B2A4A] hover:bg-[#EDF1F8]"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-[#3A4557]" />
                      {filters.dateTo ? (
                        format(new Date(filters.dateTo), "dd MMM yyyy")
                      ) : (
                        <span className="text-[#3A4557]">Select date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-[#C9D4E6]" align="start">
                    <CalendarPicker
                      mode="single"
                      selected={filters.dateTo ? new Date(filters.dateTo) : undefined}
                      onSelect={(date) => setFilters({...filters, dateTo: date ? format(date, "yyyy-MM-dd") : ""})}
                      initialFocus
                      className="bg-white text-[#1B2A4A]"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Platform */}
              <div>
                <label className="text-[#3A4557] text-xs mb-1 block">Platform</label>
                <select
                  value={filters.platform}
                  onChange={(e) => setFilters({...filters, platform: e.target.value})}
                  className="w-full h-9 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm"
                >
                  <option value="all">All Platforms</option>
                  {uniquePlatforms.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              {/* Policy Type */}
              <div>
                <label className="text-[#3A4557] text-xs mb-1 block">Case Type</label>
                <select
                  value={filters.policyType}
                  onChange={(e) => setFilters({...filters, policyType: e.target.value})}
                  className="w-full h-9 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="Fresh">Fresh</option>
                  <option value="Portability">Portability</option>
                  <option value="Renewal">Renewal</option>
                </select>
              </div>

              {/* Company */}
              <div>
                <label className="text-[#3A4557] text-xs mb-1 block">Company</label>
                <select
                  value={filters.company}
                  onChange={(e) => setFilters({...filters, company: e.target.value})}
                  className="w-full h-9 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm"
                >
                  <option value="all">All Companies</option>
                  {uniqueCompanies.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Referred By */}
              <div>
                <label className="text-[#3A4557] text-xs mb-1 block">Referred By</label>
                <select
                  value={filters.referredBy}
                  onChange={(e) => setFilters({...filters, referredBy: e.target.value})}
                  className="w-full h-9 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm"
                >
                  <option value="all">All Sources</option>
                  {uniqueReferredBy.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              {/* Insurance Category */}
              <div>
                <label className="text-[#3A4557] text-xs mb-1 block">Category</label>
                <select
                  value={filters.insuranceCategory}
                  onChange={(e) => setFilters({...filters, insuranceCategory: e.target.value})}
                  className="w-full h-9 px-3 bg-white/70 border border-[#C9D4E6] rounded-md text-[#1B2A4A] text-sm"
                >
                  <option value="all">All Categories</option>
                  <option value="Health">Health Insurance</option>
                  <option value="Life">Life Insurance</option>
                  <option value="Vehicle">Vehicle Insurance</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="summary-cards">
          <Card className="bg-gradient-to-br from-slate-500/20 to-slate-800/20 border-slate-400/30" data-testid="total-clients-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#2E3B57] text-sm">Total Clients</p>
                  <p className="text-3xl font-bold text-[#1B2A4A] mt-1" data-testid="total-clients-value">{totalClients}</p>
                </div>
                <Users className="h-8 w-8 text-[#2E3B57]" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-600/20 to-blue-900/20 border-blue-500/30" data-testid="total-policies-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#2E3B57] text-sm">Total Policies</p>
                  <p className="text-3xl font-bold text-[#1B2A4A] mt-1" data-testid="total-policies-value">{analytics.totalPolicies}</p>
                </div>
                <FileText className="h-8 w-8 text-[#2E3B57]" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-600/20 to-green-900/20 border-green-500/30" data-testid="total-premium-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-700 text-sm">Total Premium</p>
                  <p className="text-2xl font-bold text-[#1B2A4A] mt-1" data-testid="total-premium-value">{formatCurrency(analytics.totalPremium)}</p>
                </div>
                <IndianRupee className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`bg-gradient-to-br from-[#D4A017]/20 to-[#D4A017]/8 border-[#D4A017]/30 ${!commissionUnlocked ? 'cursor-pointer hover:border-[#D4A017]/50 transition-colors' : ''}`} 
            data-testid="net-commission-card"
            onClick={() => !commissionUnlocked && setShowPinModal(true)}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#A87608] text-sm flex items-center gap-2">
                    Commission Received (MIS)
                    {!commissionUnlocked && <Lock className="h-3 w-3" />}
                  </p>
                  {commissionUnlocked ? (
                    <p className="text-2xl font-bold text-[#1B2A4A] mt-1" data-testid="net-commission-value">{formatCurrency(analytics.totalCommission)}</p>
                  ) : (
                    <p className="text-2xl font-bold text-[#1B2A4A] mt-1 flex items-center gap-2" data-testid="net-commission-value">
                      ₹ ****
                      <span className="text-xs text-[#A87608] font-normal">Click to unlock</span>
                    </p>
                  )}
                </div>
                {commissionUnlocked ? (
                  <IndianRupee className="h-8 w-8 text-[#A87608]" />
                ) : (
                  <Lock className="h-8 w-8 text-[#A87608]" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-600/20 to-purple-900/20 border-purple-500/30" data-testid="avg-premium-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-700 text-sm">Avg. Premium</p>
                  <p className="text-2xl font-bold text-[#1B2A4A] mt-1" data-testid="avg-premium-value">
                    {formatCurrency(analytics.totalPolicies > 0 ? analytics.totalPremium / analytics.totalPolicies : 0)}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ===== GRAPHICAL ANALYTICS ===== */}
        {chartData && !chartLoading && (
          <>
            {/* Row 1: Premium Trends */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Monthly Premium Bar Chart */}
              <Card className="bg-white/50 border-[#C9D4E6]" data-testid="premium-trend-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-[#A87608] flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Monthly Premium Collected
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {chartData.premium_trend?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <ReBarChart data={chartData.premium_trend} barGap={2}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="label" tick={{ fill: '#475569', fontSize: 11 }} axisLine={{ stroke: '#475569' }} />
                        <YAxis tick={{ fill: '#475569', fontSize: 11 }} axisLine={{ stroke: '#475569' }}
                          tickFormatter={v => v >= 100000 ? `${(v/100000).toFixed(0)}L` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: 8, color: '#f8fafc' }}
                          labelStyle={{ color: '#94a3b8', marginBottom: 4 }}
                          formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, '']}
                        />
                        <Bar dataKey="total" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Total Premium" />
                      </ReBarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[240px] flex items-center justify-center text-[#3A4557] text-sm">No premium data yet</div>
                  )}
                </CardContent>
              </Card>

              {/* Fresh+Port vs Renewal MoM */}
              <Card className="bg-white/50 border-[#C9D4E6]" data-testid="fresh-vs-renewal-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-[#A87608] flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Fresh vs Renewal (Month-on-Month)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {chartData.premium_trend?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <ReBarChart data={chartData.premium_trend} barGap={2}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="label" tick={{ fill: '#475569', fontSize: 11 }} axisLine={{ stroke: '#475569' }} />
                        <YAxis tick={{ fill: '#475569', fontSize: 11 }} axisLine={{ stroke: '#475569' }}
                          tickFormatter={v => v >= 100000 ? `${(v/100000).toFixed(0)}L` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: 8, color: '#f8fafc' }}
                          formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, '']}
                        />
                        <Bar dataKey="fresh" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} name="Fresh + Portability" />
                        <Bar dataKey="renewal" stackId="a" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Renewal" />
                        <Legend wrapperStyle={{ fontSize: 11, color: '#94a3b8' }} />
                      </ReBarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[240px] flex items-center justify-center text-[#3A4557] text-sm">No data yet</div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Row 2: Clients + Category */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* New Clients Added */}
              <Card className="bg-white/50 border-[#C9D4E6]" data-testid="clients-trend-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-[#A87608] flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    New Clients Added
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {chartData.clients_trend?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={chartData.clients_trend}>
                        <defs>
                          <linearGradient id="clientGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="label" tick={{ fill: '#475569', fontSize: 11 }} axisLine={{ stroke: '#475569' }} />
                        <YAxis tick={{ fill: '#475569', fontSize: 11 }} axisLine={{ stroke: '#475569' }} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: 8, color: '#f8fafc' }}
                          formatter={(v) => [v, 'Clients']}
                        />
                        <Area type="monotone" dataKey="count" stroke="#8b5cf6" strokeWidth={2.5} fillOpacity={1} fill="url(#clientGrad)" name="New Clients" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[220px] flex items-center justify-center text-[#3A4557] text-sm">No client data yet</div>
                  )}
                </CardContent>
              </Card>

              {/* Policies by Category (Donut) */}
              <Card className="bg-white/50 border-[#C9D4E6]" data-testid="category-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-[#A87608] flex items-center gap-2">
                    <PieChart className="h-4 w-4" />
                    Policies by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {chartData.category_data?.length > 0 ? (
                    <div className="flex items-center gap-4">
                      <ResponsiveContainer width="55%" height={200}>
                        <RePieChart>
                          <Pie
                            data={chartData.category_data}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            dataKey="value"
                            stroke="none"
                            paddingAngle={3}
                          >
                            {chartData.category_data.map((entry, i) => {
                              const COLORS = { Health: '#22c55e', Life: '#f59e0b', Vehicle: '#3b82f6', PA: '#f43f5e' };
                              return <Cell key={i} fill={COLORS[entry.name] || '#6b7280'} />;
                            })}
                          </Pie>
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: 8, color: '#f8fafc' }}
                            formatter={(v, n) => [v, n]}
                          />
                        </RePieChart>
                      </ResponsiveContainer>
                      <div className="flex-1 space-y-3">
                        {chartData.category_data.map((item) => {
                          const COLORS = { Health: '#22c55e', Life: '#f59e0b', Vehicle: '#3b82f6', PA: '#f43f5e' };
                          const ICONS = { Health: Heart, Life: Target, Vehicle: Car, PA: Target };
                          const Ic = ICONS[item.name] || Target;
                          return (
                            <div key={item.name} className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[item.name] || '#6b7280' }} />
                              <Ic className="h-3.5 w-3.5 text-[#3A4557]" />
                              <span className="text-sm text-[#2E3B57] flex-1">{item.name}</span>
                              <span className="text-sm font-bold text-[#1B2A4A]">{item.value}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-[#3A4557] text-sm">No policies yet</div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Row 3: Upcoming Renewals Pipeline */}
            <Card className="bg-white/50 border-[#C9D4E6] mb-6" data-testid="renewals-pipeline">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-[#A87608] flex items-center gap-2">
                  <CalendarClock className="h-4 w-4" />
                  Upcoming Renewals Pipeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-red-600">{chartData.renewals?.count_30 || 0}</p>
                    <p className="text-xs text-red-700 mt-1">Within 30 Days</p>
                  </div>
                  <div className="bg-[#D4A017]/10 border border-[#D4A017]/30 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-[#A87608]">{chartData.renewals?.count_60 || 0}</p>
                    <p className="text-xs text-[#A87608] mt-1">30-60 Days</p>
                  </div>
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-600">{chartData.renewals?.count_90 || 0}</p>
                    <p className="text-xs text-emerald-700 mt-1">60-90 Days</p>
                  </div>
                </div>
                
                {(chartData.renewals?.count_30 > 0 || chartData.renewals?.count_60 > 0 || chartData.renewals?.count_90 > 0) ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {[...chartData.renewals?.within_30 || [], ...chartData.renewals?.within_60 || [], ...chartData.renewals?.within_90 || []].map((r, i) => (
                      <div key={i} className="bg-[#EDF1F8]/50 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1B2A4A] truncate">{r.client_name}</p>
                          <p className="text-xs text-[#3A4557] truncate">{r.insurance_company} - {r.plan_name}</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          <p className={`text-sm font-bold ${r.days_left <= 7 ? 'text-red-600' : r.days_left <= 30 ? 'text-[#A87608]' : 'text-emerald-600'}`}>
                            {r.days_left}d
                          </p>
                          <p className="text-[10px] text-[#3A4557]">{r.end_date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#3A4557] text-sm py-4">No upcoming renewals in the next 90 days</p>
                )}
              </CardContent>
            </Card>

            {/* Yearly Premium Summary */}
            {chartData.yearly_trend?.length > 1 && (
              <Card className="bg-white/50 border-[#C9D4E6] mb-6" data-testid="yearly-premium-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-[#A87608] flex items-center gap-2">
                    <CalendarDays className="h-4 w-4" />
                    Yearly Premium Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <ReBarChart data={chartData.yearly_trend} barSize={50}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="year" tick={{ fill: '#475569', fontSize: 12 }} axisLine={{ stroke: '#475569' }} />
                      <YAxis tick={{ fill: '#475569', fontSize: 11 }} axisLine={{ stroke: '#475569' }}
                        tickFormatter={v => v >= 100000 ? `${(v/100000).toFixed(0)}L` : `${(v/1000).toFixed(0)}K`} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: 8, color: '#f8fafc' }}
                        formatter={(v) => [`₹${Number(v).toLocaleString('en-IN')}`, 'Premium']}
                      />
                      <Bar dataKey="total" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                    </ReBarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Insurance Book Projection - Collapsible */}
        <Card className="bg-white/50 border-[#C9D4E6] mb-6" data-testid="insurance-book-section">
          <CardHeader 
            className="pb-3 cursor-pointer hover:bg-[#EDF1F8]/30 transition-colors rounded-t-lg"
            onClick={() => setShowInsuranceBook(!showInsuranceBook)}
          >
            <CardTitle className="text-lg text-[#A87608] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Insurance Book Projection
                <span className="text-xs text-[#3A4557] font-normal ml-2">
                  ({analytics.annualPolicyCount} Annual + {analytics.multiYearPolicyCount} Multi-year policies)
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#3A4557] font-normal">
                  Next Year Book: <span className="text-purple-600 font-semibold">{formatCurrency(analytics.nextYearConfirmed + analytics.nextYearExpectedRenewals)}</span>
                </span>
                {showInsuranceBook ? (
                  <ChevronUp className="h-5 w-5 text-[#3A4557]" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-[#3A4557]" />
                )}
              </div>
            </CardTitle>
          </CardHeader>
          
          {showInsuranceBook && (
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Current Year Commissionable */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-900/10 border border-emerald-500/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Target className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-[#3A4557] text-xs">Current Year</p>
                      <p className="text-emerald-600 text-sm font-medium">Commissionable Premium</p>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-[#1B2A4A] mt-2">
                    {formatCurrency(analytics.firstYearCommissionable)}
                  </p>
                  <p className="text-[#3A4557] text-xs mt-1">
                    Premium ÷ Policy Years
                  </p>
                </div>

                {/* Next Year - Confirmed (Multi-year) */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-[#EDF1F8]/10 to-blue-900/10 border border-blue-500/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-lg bg-[#EDF1F8]/40 flex items-center justify-center">
                      <CalendarCheck className="h-5 w-5 text-[#2E3B57]" />
                    </div>
                    <div>
                      <p className="text-[#3A4557] text-xs">Next Year</p>
                      <p className="text-[#2E3B57] text-sm font-medium">Confirmed Book</p>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-[#1B2A4A] mt-2">
                    {formatCurrency(analytics.nextYearConfirmed)}
                  </p>
                  <p className="text-[#3A4557] text-xs mt-1">
                    From {analytics.multiYearPolicyCount} multi-year policies
                  </p>
                </div>

                {/* Next Year - Expected Renewals (Annual) */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-[#D4A017]/10 to-[#D4A017]/5 border border-[#D4A017]/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-lg bg-[#D4A017]/15 flex items-center justify-center">
                      <Repeat className="h-5 w-5 text-[#A87608]" />
                    </div>
                    <div>
                      <p className="text-[#3A4557] text-xs">Next Year</p>
                      <p className="text-[#A87608] text-sm font-medium">Expected Renewals</p>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-[#1B2A4A] mt-2">
                    {formatCurrency(analytics.nextYearExpectedRenewals)}
                  </p>
                  <p className="text-[#3A4557] text-xs mt-1">
                    From {analytics.annualPolicyCount} annual policies
                  </p>
                </div>

                {/* Total Next Year Book */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-purple-900/10 border border-purple-500/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-10 w-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-[#3A4557] text-xs">Next Year</p>
                      <p className="text-purple-600 text-sm font-medium">Total Book</p>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-[#1B2A4A] mt-2">
                    {formatCurrency(analytics.nextYearConfirmed + analytics.nextYearExpectedRenewals)}
                  </p>
                  <p className="text-[#3A4557] text-xs mt-1">
                    Confirmed + Expected
                  </p>
                </div>
              </div>

              {/* Year 2+ Pipeline - Only show if there are 3+ year policies */}
              {analytics.year2PlusPipeline > 0 && (
                <div className="mt-4 p-4 rounded-xl bg-[#EDF1F8]/50 border border-[#C9D4E6]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-[#E2E9F4] flex items-center justify-center">
                        <CalendarClock className="h-5 w-5 text-[#3A4557]" />
                      </div>
                      <div>
                        <p className="text-[#2E3B57] font-medium">Year 2+ Pipeline</p>
                        <p className="text-[#3A4557] text-xs">Confirmed future book from 3+ year policies</p>
                      </div>
                    </div>
                    <p className="text-xl font-bold text-[#1B2A4A]">
                      {formatCurrency(analytics.year2PlusPipeline)}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Analytics Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          
          {/* By Case Type */}
          <Card className="bg-white/50 border-[#C9D4E6]">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-[#A87608] flex items-center gap-2">
                <PieChart className="h-5 w-5" />
                By Case Type
                <span className="text-xs text-[#3A4557] font-normal ml-2">(Click to see details)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(analytics.byPolicyType).map(([type, data]) => {
                  const percentage = analytics.totalPolicies > 0 
                    ? ((data.count / analytics.totalPolicies) * 100).toFixed(1) 
                    : 0;
                  const color = type === 'Fresh' ? 'green' : type === 'Portability' ? 'purple' : 'blue';
                  
                  return (
                    <div 
                      key={type} 
                      className="p-3 bg-[#EDF1F8]/50 rounded-lg cursor-pointer hover:bg-[#E2E9F4]/50 transition-colors hover:border-[#D4A017]/50 border border-transparent"
                      onClick={() => handleDrillDown("policyType", type, `${type} Policies`)}
                      data-testid={`case-type-${type.toLowerCase()}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium bg-${color}-500/20 text-${color}-700`}>
                            {type}
                          </span>
                          <span className="text-[#1B2A4A] font-medium">{data.count} policies</span>
                        </div>
                        <span className="text-[#3A4557]">{percentage}%</span>
                      </div>
                      <div className="h-2 bg-[#E2E9F4] rounded-full overflow-hidden">
                        <div 
                          className={`h-full bg-${color}-500 rounded-full transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-2 text-sm">
                        <span className="text-green-600">{formatCurrency(data.premium)}</span>
                        <span className="text-[#A87608]">{commissionUnlocked ? formatCurrency(data.commission) : '₹ ****'}</span>
                      </div>
                    </div>
                  );
                })}
                {Object.keys(analytics.byPolicyType).length === 0 && (
                  <p className="text-[#3A4557] text-center py-4">No data available</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* By Referred Source */}
          <Card className="bg-white/50 border-[#C9D4E6]">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-[#A87608] flex items-center gap-2">
                <Users className="h-5 w-5" />
                By Referral Source
                <span className="text-xs text-[#3A4557] font-normal ml-2">(Click to see details)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(analytics.byReferredBy).map(([source, data]) => {
                  const percentage = analytics.totalPolicies > 0 
                    ? ((data.count / analytics.totalPolicies) * 100).toFixed(1) 
                    : 0;
                  
                  return (
                    <div 
                      key={source} 
                      className="p-3 bg-[#EDF1F8]/50 rounded-lg cursor-pointer hover:bg-[#E2E9F4]/50 transition-colors hover:border-[#D4A017]/50 border border-transparent"
                      onClick={() => handleDrillDown("referredBy", source, `Referred by ${source}`)}
                      data-testid={`referral-source-${source.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            source === 'Beshak' ? 'bg-[#EDF1F8]/40 text-[#2E3B57]' :
                            source === 'Others' ? 'bg-orange-500/20 text-orange-700' :
                            'bg-[#EDF1F8]/20 text-[#2E3B57]'
                          }`}>
                            {source}
                          </span>
                          <span className="text-[#1B2A4A] font-medium">{data.count} policies</span>
                        </div>
                        <span className="text-[#3A4557]">{percentage}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600">{formatCurrency(data.premium)}</span>
                        <span className="text-[#A87608]">{commissionUnlocked ? formatCurrency(data.commission) : '₹ ****'}</span>
                      </div>
                    </div>
                  );
                })}
                {Object.keys(analytics.byReferredBy).length === 0 && (
                  <p className="text-[#3A4557] text-center py-4">No data available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Insurance Category Analysis */}
        <div className="mb-6">
          <Card className="bg-white/50 border-[#C9D4E6]">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-[#A87608] flex items-center gap-2">
                <PieChart className="h-5 w-5" />
                By Insurance Category
                <span className="text-xs text-[#3A4557] font-normal ml-2">(Click to see details)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Health Insurance */}
                <div 
                  className={`p-4 rounded-lg border cursor-pointer transition-all hover:scale-[1.02] ${
                    analytics.byInsuranceCategory['Health']?.count > 0 
                      ? 'bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-400' 
                      : 'bg-[#EDF1F8]/50 border-[#C9D4E6] hover:border-[#C9D4E6]'
                  }`}
                  onClick={() => handleDrillDown("category", "Health", "Health Insurance Policies")}
                  data-testid="category-health"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-12 w-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <Heart className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-emerald-600 font-bold text-lg">
                        {analytics.byInsuranceCategory['Health']?.count || 0}
                      </p>
                      <p className="text-[#3A4557] text-sm">Health Insurance</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#3A4557]">Premium</span>
                      <span className="text-emerald-600 font-medium">
                        {formatCurrency(analytics.byInsuranceCategory['Health']?.premium || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#3A4557]">Commission</span>
                      <span className="text-[#A87608] font-medium">
                        {commissionUnlocked 
                          ? formatCurrency(analytics.byInsuranceCategory['Health']?.commission || 0)
                          : '₹ ****'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {/* Life Insurance */}
                <div 
                  className={`p-4 rounded-lg border cursor-pointer transition-all hover:scale-[1.02] ${
                    analytics.byInsuranceCategory['Life']?.count > 0 
                      ? 'bg-[#EDF1F8]/30 border-blue-500/30 hover:border-blue-400' 
                      : 'bg-[#EDF1F8]/50 border-[#C9D4E6] hover:border-[#C9D4E6]'
                  }`}
                  onClick={() => handleDrillDown("category", "Life", "Life Insurance Policies")}
                  data-testid="category-life"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-12 w-12 rounded-xl bg-[#EDF1F8]/40 flex items-center justify-center">
                      <User className="h-6 w-6 text-[#2E3B57]" />
                    </div>
                    <div>
                      <p className="text-[#2E3B57] font-bold text-lg">
                        {analytics.byInsuranceCategory['Life']?.count || 0}
                      </p>
                      <p className="text-[#3A4557] text-sm">Life Insurance</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#3A4557]">Premium</span>
                      <span className="text-[#2E3B57] font-medium">
                        {formatCurrency(analytics.byInsuranceCategory['Life']?.premium || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#3A4557]">Commission</span>
                      <span className="text-[#A87608] font-medium">
                        {commissionUnlocked 
                          ? formatCurrency(analytics.byInsuranceCategory['Life']?.commission || 0)
                          : '₹ ****'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {/* Vehicle Insurance */}
                <div 
                  className={`p-4 rounded-lg border cursor-pointer transition-all hover:scale-[1.02] ${
                    analytics.byInsuranceCategory['Vehicle']?.count > 0 
                      ? 'bg-[#D4A017]/10 border-[#D4A017]/30 hover:border-[#D4A017]' 
                      : 'bg-[#EDF1F8]/50 border-[#C9D4E6] hover:border-[#C9D4E6]'
                  }`}
                  onClick={() => handleDrillDown("category", "Vehicle", "Vehicle Insurance Policies")}
                  data-testid="category-vehicle"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-12 w-12 rounded-xl bg-[#D4A017]/15 flex items-center justify-center">
                      <Car className="h-6 w-6 text-[#A87608]" />
                    </div>
                    <div>
                      <p className="text-[#A87608] font-bold text-lg">
                        {analytics.byInsuranceCategory['Vehicle']?.count || 0}
                      </p>
                      <p className="text-[#3A4557] text-sm">Vehicle Insurance</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[#3A4557]">Premium</span>
                      <span className="text-[#A87608] font-medium">
                        {formatCurrency(analytics.byInsuranceCategory['Vehicle']?.premium || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[#3A4557]">Commission</span>
                      <span className="text-[#A87608] font-medium">
                        {commissionUnlocked 
                          ? formatCurrency(analytics.byInsuranceCategory['Vehicle']?.commission || 0)
                          : '₹ ****'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Platform & Company Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          
          {/* By Platform */}
          <Card className="bg-white/50 border-[#C9D4E6]">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-[#A87608] flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                By Platform
                <span className="text-xs text-[#3A4557] font-normal ml-2">(Click row to see details)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Swipeable hint>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#C9D4E6]">
                      <th className="text-left py-2 text-[#3A4557]">Platform</th>
                      <th className="text-center py-2 text-[#3A4557]">Count</th>
                      <th className="text-right py-2 text-[#3A4557]">Premium</th>
                      <th className="text-right py-2 text-[#3A4557]">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedByPlatform.map(([platform, data]) => (
                      <tr 
                        key={platform} 
                        className="border-b border-[#C9D4E6] cursor-pointer hover:bg-[#E2E9F4]/50 transition-colors"
                        onClick={() => handleDrillDown("platform", platform, `Platform: ${platform}`)}
                        data-testid={`platform-row-${platform.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <td className="py-2 text-[#1B2A4A]">{platform}</td>
                        <td className="py-2 text-center">
                          <span className="px-2 py-0.5 bg-[#EDF1F8]/40 text-[#2E3B57] rounded-full text-xs">
                            {data.count}
                          </span>
                        </td>
                        <td className="py-2 text-right text-green-600">{formatCurrency(data.premium)}</td>
                        <td className="py-2 text-right text-[#A87608]">{commissionUnlocked ? formatCurrency(data.commission) : '₹ ****'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sortedByPlatform.length === 0 && (
                  <p className="text-[#3A4557] text-center py-4">No data available</p>
                )}
              </Swipeable>
            </CardContent>
          </Card>

          {/* By Company (Top performers) */}
          <Card className="bg-white/50 border-[#C9D4E6]">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-[#A87608] flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                By Insurance Company
                <span className="text-xs text-[#3A4557] font-normal ml-2">(Click row to see details)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Swipeable hint>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#C9D4E6]">
                      <th className="text-left py-2 text-[#3A4557]">Company</th>
                      <th className="text-center py-2 text-[#3A4557]">Count</th>
                      <th className="text-right py-2 text-[#3A4557]">Premium</th>
                      <th className="text-right py-2 text-[#3A4557]">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedByCompany.slice(0, 10).map(([company, data]) => (
                      <tr 
                        key={company} 
                        className="border-b border-[#C9D4E6] cursor-pointer hover:bg-[#E2E9F4]/50 transition-colors"
                        onClick={() => handleDrillDown("company", company, `Company: ${company}`)}
                        data-testid={`company-row-${company.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <td className="py-2 text-[#1B2A4A] truncate max-w-[150px]" title={company}>{company}</td>
                        <td className="py-2 text-center">
                          <span className="px-2 py-0.5 bg-[#EDF1F8]/40 text-[#2E3B57] rounded-full text-xs">
                            {data.count}
                          </span>
                        </td>
                        <td className="py-2 text-right text-green-600">{formatCurrency(data.premium)}</td>
                        <td className="py-2 text-right text-[#A87608]">{commissionUnlocked ? formatCurrency(data.commission) : '₹ ****'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sortedByCompany.length === 0 && (
                  <p className="text-[#3A4557] text-center py-4">No data available</p>
                )}
              </Swipeable>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Trend */}
        <Card className="bg-white/50 border-[#C9D4E6]">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-[#A87608] flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Monthly Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Swipeable hint>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#C9D4E6]">
                    <th className="text-left py-3 px-4 text-[#3A4557]">Month</th>
                    <th className="text-center py-3 px-4 text-[#3A4557]">Policies</th>
                    <th className="text-right py-3 px-4 text-[#3A4557]">Premium</th>
                    <th className="text-right py-3 px-4 text-[#3A4557]">Commission</th>
                    <th className="text-right py-3 px-4 text-[#3A4557]">Avg Premium</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedByMonth.map(([month, data]) => {
                    const avgPremium = data.count > 0 ? data.premium / data.count : 0;
                    const monthName = month !== "Unknown" ? new Date(month + "-01").toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : month;
                    
                    return (
                      <tr key={month} className="border-b border-[#C9D4E6] hover:bg-[#EDF1F8]/50">
                        <td className="py-3 px-4 text-[#1B2A4A]">{monthName}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="px-2 py-1 bg-[#EDF1F8]/40 text-[#2E3B57] rounded-full text-sm">
                            {data.count}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-green-600 font-medium">
                          {formatCurrency(data.premium)}
                        </td>
                        <td className="py-3 px-4 text-right text-[#A87608] font-medium">
                          {commissionUnlocked ? formatCurrency(data.commission) : '₹ ****'}
                        </td>
                        <td className="py-3 px-4 text-right text-[#3A4557]">
                          {formatCurrency(avgPremium)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {sortedByMonth.length === 0 && (
                <p className="text-[#3A4557] text-center py-8">No data available. Map some policies to see your business analytics.</p>
              )}
            </Swipeable>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
